import { FC, useCallback, useMemo, useState } from 'react'
import { Modal } from '../../components'
import { useTranslation } from 'react-i18next'
import { IdoorAccess, IdoorNfc, IdoorPin } from '../../API/types'
import dayjs, { Dayjs } from 'dayjs'
import { appUrl, DATETIME_FORMAT } from '../../API/utils'
import { Button, Divider, Grid, IconButton, InputBase, Paper, TextField } from '@mui/material'
import { LocalizationProvider, StaticDateTimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { AddCircleOutline, ContentCopy, DeleteOutline, Key, OpenInNew } from '@mui/icons-material'
import { DataGrid, GridColDef, GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid'

interface DoorAccessCreateProps {
  handleClickClose: () => void
  createDoorAccess: (rawRoomsData: IdoorAccess) => void
  roomsListData: IdoorAccess[]
  id: string
}

const DoorAccessCreate: FC<DoorAccessCreateProps> = ({ handleClickClose, createDoorAccess, roomsListData, id }) => {
  const { t } = useTranslation('')

  const roomsData = useMemo(() => {
    return roomsListData.find((roomsData) => roomsData.id === id)
  }, [id, roomsListData])

  const urlKey = appUrl + '?key=' + roomsData?.key

  const pinListGridRef = useGridApiRef()
  const nfcListGridRef = useGridApiRef()

  const [dateFrom, setDateFrom] = useState<Dayjs>(roomsData?.dateFrom ? dayjs(roomsData?.dateFrom) : dayjs())
  const [dateTo, setDateTo] = useState<Dayjs>(roomsData?.dateTo ? dayjs(roomsData?.dateTo) : dayjs())
  const [comments, setComments] = useState<string>(roomsData?.comments ? roomsData?.comments : '')
  const [newPin, setMewPin] = useState<string>('')
  const [newNFC, setMewNFC] = useState<string>('')
  const [pinList, setPinList] = useState<IdoorPin[]>(roomsData?.pinList ? roomsData?.pinList : [])
  const [nfcList, setNfcList] = useState<IdoorNfc[]>(roomsData?.nfcList ? roomsData?.nfcList : [])

  const handleClose = useCallback(() => {
    handleClickClose()
  }, [handleClickClose])

  const handleAccept = useCallback(async () => {
    const rawRoomsData: IdoorAccess = {
      id: id,
      dateFrom: dateFrom.toString(),
      dateTo: dateTo.toString(),
      disabled: false,
      comments: comments,
      pinList: pinList,
      nfcList: nfcList,
    }

    await createDoorAccess(rawRoomsData)
    handleClickClose()
  }, [handleClickClose, createDoorAccess, comments, dateFrom, dateTo, id, pinList, nfcList])

  const addMewPin = useCallback(async () => {
    let newId = '1'
    if (pinList.length > 0) {
      newId = (pinList[pinList.length - 1].id + 1).toString()
    }

    const rawPin: IdoorPin = {
      id: newId,
      value: newPin,
    }

    pinListGridRef.current.updateRows([rawPin])

    setPinList([...pinList, rawPin])
    setMewPin('')
  }, [pinList, newPin, pinListGridRef])

  const addNewNFC = useCallback(async () => {
    let newId = '1'
    if (nfcList.length > 0) {
      newId = (nfcList[nfcList.length - 1].id + 1).toString()
    }

    const rawPin: IdoorPin = {
      id: newId,
      value: newNFC,
    }

    nfcListGridRef.current.updateRows([rawPin])

    setNfcList([...nfcList, rawPin])
    setMewNFC('')
  }, [nfcList, newNFC, nfcListGridRef])

  const handleDeletePin = useCallback(
    async (id: string) => {
      pinListGridRef.current.updateRows([{ id: id, _action: 'delete' }])

      var filteredArray = pinList.filter((pin) => pin.id !== id)

      setPinList([...filteredArray])
    },
    [pinList, pinListGridRef],
  )

  const handleDeleteNfc = useCallback(
    async (id: string) => {
      nfcListGridRef.current.updateRows([{ id: id, _action: 'delete' }])

      var filteredArray = nfcList.filter((pin) => pin.id !== id)

      console.log(nfcList)

      setNfcList([...filteredArray])
    },
    [nfcList, nfcListGridRef],
  )

  const pinListColumns: GridColDef[] = [
    { field: 'value', headerName: t('PIN / Parole'), width: 330 },
    {
      field: 'actions',
      headerName: t(''),
      width: 30,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <IconButton
              color='error'
              component='div'
              sx={{}}
              onClick={() => {
                handleDeletePin(params.row.id)
              }}
            >
              <DeleteOutline />
            </IconButton>
          </>
        )
      },
    },
  ]

  const nfcListColumns: GridColDef[] = [
    { field: 'value', headerName: t('NFC / Key'), width: 330 },
    {
      field: 'actions',
      headerName: t(''),
      width: 30,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <IconButton
              color='error'
              component='div'
              sx={{}}
              onClick={() => {
                handleDeleteNfc(params.row.id)
              }}
            >
              <DeleteOutline />
            </IconButton>
          </>
        )
      },
    },
  ]

  return (
    <Modal
      maxWidth={'lg'}
      isOpen={true}
      handleClose={handleClose}
      handleAccept={handleAccept}
      dialogTitle={
        roomsData?.comments
          ? t(
              `${roomsData?.comments} - ${dayjs(roomsData?.dateFrom).format(DATETIME_FORMAT)} - ${dayjs(
                roomsData?.dateTo,
              ).format(DATETIME_FORMAT)}`,
            )
          : t(`Izveidot jaunu`)
      }
      acceptTitle={roomsData?.comments ? t(`Saglabāt izmaiņas`) : t(`Izveidot jaunu`)}
      closeTitle={t(`Atcelt`)}
    >
      <Grid container spacing={0} sx={{ background: '#121212', paddingTop: 1 }}>
        <Grid item xs={12} sx={{}}>
          <div style={{ textAlign: 'center', padding: '0 5px 5px 5px' }}>
            <TextField
              label={'Vārds, telefons, kommentārs'}
              size={'small'}
              fullWidth
              variant='outlined'
              multiline
              rows={2}
              value={comments}
              onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setComments(event.target.value)
              }}
            />
          </div>
        </Grid>
        <Grid item xs={6} sx={{}}>
          <div style={{ minHeight: 300 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} localeText={{ dateTimePickerToolbarTitle: 'Date from' }}>
              <StaticDateTimePicker
                value={dateFrom}
                onChange={(newValue) => (newValue ? setDateFrom(newValue) : null)}
                disableHighlightToday={true}
                orientation={'landscape'}
                ampm={false}
                ampmInClock={false}
                autoFocus={false}
                disablePast={true}
                slots={{
                  tabs: undefined,
                  calendarHeader: undefined,
                }}
                slotProps={{ actionBar: { actions: [] } }}
                sx={{
                  '& .MuiDateCalendar-root': {
                    maxHeight: 300,
                  },
                  '& .MuiPickersLayout-toolbar': {
                    paddingTop: 0,
                    paddingRight: 0,
                    paddingLeft: 1,
                  },
                  '& .MuiPickersCalendarHeader-root': {
                    marginTop: 0,
                    marginRight: 0,
                  },
                  '& .MuiTypography-overline': {
                    fontWeight: 600,
                    fontSize: '12pt',
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        </Grid>
        <Grid item xs={6} sx={{}}>
          <div style={{ minHeight: 300 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} localeText={{ dateTimePickerToolbarTitle: 'Date to' }}>
              <StaticDateTimePicker
                value={dateTo}
                onChange={(newValue) => (newValue ? setDateTo(newValue) : null)}
                disableHighlightToday={true}
                orientation={'landscape'}
                ampm={false}
                ampmInClock={false}
                autoFocus={false}
                disablePast={true}
                slots={{
                  tabs: undefined,
                  calendarHeader: undefined,
                }}
                slotProps={{ actionBar: { actions: [] } }}
                sx={{
                  '& .MuiDateCalendar-root': {
                    maxHeight: 300,
                  },
                  '& .MuiPickersLayout-toolbar': {
                    paddingTop: 0,
                  },
                  '& .MuiPickersCalendarHeader-root': {
                    marginTop: 0,
                  },
                  '& .MuiTypography-overline': {
                    fontWeight: 600,
                    fontSize: '12pt',
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        </Grid>
        <Grid item xs={12} sx={{ padding: '0 15px' }}>
          <Paper
            style={{ color: 'gray' }}
            component='form'
            sx={{ p: '0px 4px', display: 'flex', alignItems: 'center' }}
          >
            <Key />
            <InputBase
              style={{ color: 'gray' }}
              sx={{ ml: 1, flex: 1 }}
              value={urlKey}
              color={'primary'}
              inputProps={{ 'aria-label': 'search google maps' }}
            />
            <IconButton onClick={() => navigator.clipboard.writeText(urlKey)} color='primary' sx={{ p: '10px' }}>
              <ContentCopy />
            </IconButton>
            <Divider sx={{ height: 28, m: 0.5 }} orientation='vertical' />
            <IconButton href={urlKey} component={'a'} target={'_blank'} color='primary' sx={{ p: '10px' }}>
              <OpenInNew />
            </IconButton>
          </Paper>
        </Grid>
        <Grid item xs={6} sx={{ padding: 2 }}>
          <DataGrid
            //style={{ width: 370 }}
            apiRef={pinListGridRef}
            rows={pinList}
            columns={pinListColumns}
            rowSelection={false}
            disableColumnMenu={true}
            hideFooter={true}
            autoHeight={true}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 100 },
              },
            }}
            pageSizeOptions={[50, 10]}
          />
          <Grid container spacing={0} sx={{ background: '#121212', paddingTop: 1 }}>
            <Grid item xs={7} sx={{}}>
              <TextField
                style={{ paddingRight: 10 }}
                label={'Jauns PIN / Parole'}
                size={'small'}
                fullWidth
                variant='outlined'
                value={newPin}
                onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setMewPin(event.target.value)
                }}
              />
            </Grid>
            <Grid item xs={5} sx={{}}>
              <Button
                style={{ height: 40 }}
                color={'success'}
                fullWidth
                size={'medium'}
                variant='outlined'
                startIcon={<AddCircleOutline />}
                onClick={addMewPin}
              >
                {t('Pievienot')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6} sx={{ padding: 2 }}>
          <DataGrid
            //style={{ width: 370 }}
            apiRef={nfcListGridRef}
            rows={nfcList}
            columns={nfcListColumns}
            rowSelection={false}
            disableColumnMenu={true}
            hideFooter={true}
            autoHeight={true}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 100 },
              },
            }}
            pageSizeOptions={[50, 10]}
          />
          <Grid container spacing={0} sx={{ background: '#121212', paddingTop: 1 }}>
            <Grid item xs={7} sx={{}}>
              <TextField
                style={{ paddingRight: 10 }}
                label={'Jauns NFC / Key'}
                size={'small'}
                fullWidth
                variant='outlined'
                value={newNFC}
                onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setMewNFC(event.target.value)
                }}
              />
            </Grid>
            <Grid item xs={5} sx={{}}>
              <Button
                style={{ height: 40 }}
                color={'success'}
                fullWidth
                variant='outlined'
                startIcon={<AddCircleOutline />}
                onClick={addNewNFC}
              >
                {t('Pievienot')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  )
}

export default DoorAccessCreate
